import React from 'react';
import { UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faCheck, faCoins, faCreditCard, faPlus, faPrint, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import classes from './order-actions-modal.module.scss';
import { PendingApprovalBadge } from '../shared/PendingApprovalBadge';

export type OrderActionKey = 'generalItem' | 'printLog' | 'reprint' | 'tipOnly';

export type OrderActionsModalProps = {
    opened: boolean;
    onClose: () => void;
    isVip: boolean;
    isManager: boolean;
    isTakeAway: boolean;
    onToggleVip: (next: boolean) => void;
    hasDeposit: boolean;
    onToggleDeposit: (next: boolean) => void;
    waiterName?: string | null;
    onSelectAction?: (key: OrderActionKey) => void;
    generalItemHasPending?: boolean;
    canAddTipOnly?: boolean;
};

const ACTIONS_LABEL = 'פעולות';
const CLOSE_LABEL = 'סגור';
const VIP_ORDER_LABEL = 'סמן הזמנה כ-VIP';
const DEPOSIT_LABEL = 'סמן הזמנה עם פיקדון';
const CLOSE_GLYPH = '×';

const OrderActionsModalComponent: React.FC<OrderActionsModalProps> = ({
    opened,
    onClose,
    isVip,
    isManager,
    isTakeAway,
    onToggleVip,
    hasDeposit,
    onToggleDeposit,
    waiterName = null,
    onSelectAction,
    generalItemHasPending = false,
    canAddTipOnly = false,
}) => {
    if (!opened) return null;

    return (
        <div
            className={classes.overlay}
            role="dialog"
            aria-modal="true"
            aria-label={ACTIONS_LABEL}
            dir="rtl"
            onClick={onClose}
        >
            <div className={classes.modal} onClick={(event) => event.stopPropagation()}>
                <header className={classes.header}>
                    <span className={classes.title}>
                        {ACTIONS_LABEL}
                        {waiterName ? <span className={classes.waiterName}> - {waiterName}</span> : null}
                    </span>
                    <UnstyledButton component="button" type="button" className={classes.closeBtn} onClick={onClose} aria-label={CLOSE_LABEL} data-testid="order-action-close">
                        {CLOSE_GLYPH}
                    </UnstyledButton>
                </header>

                <div className={classes.grid}>
                    <UnstyledButton
                        component="button"
                        type="button"
                        className={`${classes.card} ${classes.cardGeneral}`}
                        onClick={() => onSelectAction?.('generalItem')}
                        data-testid="order-action-generalItem"
                    >
                        {generalItemHasPending && (
                            <PendingApprovalBadge className={classes.pendingBadge} focusable={false} stopPropagation />
                        )}
                        <span className={classes.iconWrap}>
                            <FontAwesomeIcon icon={faPlus as IconProp} />
                        </span>
                        <span className={classes.cardTitle}>פריט כללי</span>
                    </UnstyledButton>

                    {canAddTipOnly && (
                        <UnstyledButton
                            component="button"
                            type="button"
                            className={`${classes.card} ${classes.cardTipOnly}`}
                            onClick={() => onSelectAction?.('tipOnly')}
                            data-testid="order-action-tipOnly"
                        >
                            <span className={classes.iconWrap}>
                                <FontAwesomeIcon icon={faCoins as IconProp} />
                            </span>
                            <span className={classes.cardTitle}>תוספת טיפ</span>
                        </UnstyledButton>
                    )}

                    <UnstyledButton
                        component="button"
                        type="button"
                        className={`${classes.card} ${classes.cardPrintLog}`}
                        onClick={() => onSelectAction?.('printLog')}
                        data-testid="order-action-printLog"
                    >
                        <span className={classes.iconWrap}>
                            <FontAwesomeIcon icon={faPrint as IconProp} />
                        </span>
                        <span className={classes.cardTitle}>לוג הדפסות</span>
                    </UnstyledButton>

                    {!isTakeAway && isManager && (
                        <UnstyledButton
                            component="button"
                            type="button"
                            className={`${classes.card} ${classes.cardReprint}`}
                            onClick={() => onSelectAction?.('reprint')}
                            data-testid="order-action-reprint"
                        >
                            <span className={classes.iconWrap}>
                                <FontAwesomeIcon icon={faRotateRight as IconProp} />
                            </span>
                            <span className={classes.cardTitle}>הדפסה חוזרת</span>
                        </UnstyledButton>
                    )}

                    {!isTakeAway && (
                        <UnstyledButton
                            component="button"
                            type="button"
                            className={`${classes.card} ${classes.cardDeposit} ${hasDeposit ? classes.cardDepositOn : ''}`}
                            onClick={() => onToggleDeposit(!hasDeposit)}
                            role="switch"
                            aria-checked={hasDeposit}
                            aria-label={DEPOSIT_LABEL}
                            data-testid="order-deposit-toggle"
                        >
                            <span className={classes.iconWrap}>
                                <FontAwesomeIcon icon={faCreditCard as IconProp} />
                            </span>
                            <span className={classes.cardTitle}>פיקדון</span>
                            <span className={classes.depositCheck} aria-hidden="true">
                                {hasDeposit && <FontAwesomeIcon icon={faCheck as IconProp} />}
                            </span>
                        </UnstyledButton>
                    )}

                    <UnstyledButton
                        component="button"
                        type="button"
                        className={`${classes.card} ${classes.cardVip} ${isVip ? classes.cardVipOn : ''}`}
                        onClick={() => onToggleVip(!isVip)}
                        role="switch"
                        aria-checked={isVip}
                        aria-label={VIP_ORDER_LABEL}
                        data-testid="order-vip-toggle"
                    >
                        <span className={classes.iconWrap}>
                            <FontAwesomeIcon icon={faCrown as IconProp} />
                        </span>
                        <span className={classes.cardTitle}>VIP</span>
                        <span className={classes.vipCheck} aria-hidden="true">
                            {isVip && <FontAwesomeIcon icon={faCheck as IconProp} />}
                        </span>
                    </UnstyledButton>
                </div>
            </div>
        </div>
    );
};

export const OrderActionsModal = React.memo(OrderActionsModalComponent);
